import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import {
  TranslationKeys,
  TranslationService,
} from '../../../core/services/translation.service';
import {
  WordUsageService,
  WordUsageStats,
  QuotaSettings,
} from '../../../core/services/word-usage.service';

@Component({
  selector: 'app-word-usage-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './word-usage-modal.component.html',
  styleUrls: ['./word-usage-modal.component.css'],
})
export class WordUsageModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();

  currentStats: WordUsageStats | null = null;
  currentSettings: QuotaSettings | null = null;
  editingSettings = false;
  showRemainingView = false;

  // Translations
  translations: TranslationKeys = {} as TranslationKeys;
  private translationSubscription?: Subscription;

  // Store the word count when user last purchased words (for remaining view calculation)
  wordsUsedAtLastPurchase = 0;

  // Cache for calculations to prevent multiple function calls
  private _cachedWordPercentage: number | null = null;
  private _cachedUsageBarWidth: number | null = null;
  private _cachedUsageBarColor: string | null = null;
  private _cachedWordsText: string | null = null;
  private _cachedUsageDisplay: any = null;

  // Form data for editing settings
  editForm = {
    wordLimit: 2000,
    resetPeriod: 'monthly' as 'daily' | 'weekly' | 'monthly' | 'never',
    enabled: true, // Enable quota system by default
  };

  private subscription?: Subscription;

  constructor(
    private wordUsageService: WordUsageService,
    private translationService: TranslationService
  ) {}

  private clearCache() {
    this._cachedWordPercentage = null;
    this._cachedUsageBarWidth = null;
    this._cachedUsageBarColor = null;
    this._cachedWordsText = null;
    this._cachedUsageDisplay = null;
  }

  ngOnInit(): void {
    console.log('🔧 WordUsageModalComponent ngOnInit called');
    this.subscription = this.wordUsageService.usageStats$.subscribe((stats) => {
      this.currentStats = stats;
      this.clearCache(); // Clear cache when stats change

      // Load toggle preference from user data
      if (stats) {
        this.showRemainingView = stats.showRemainingView || false;
        this.wordsUsedAtLastPurchase = stats.wordsUsedAtLastPurchase || 0;
      }
    });

    this.subscription.add(
      this.wordUsageService.quotaSettings$.subscribe((settings) => {
        this.currentSettings = settings;
        this.clearCache(); // Clear cache when settings change
        this.editForm = { ...settings };
      })
    );

    // Subscribe to translations
    this.translations = this.translationService.getAllTranslations();
    this.translationSubscription = this.translationService.currentLanguage$.subscribe(() => {
      this.translations = this.translationService.getAllTranslations();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('🔧 WordUsageModalComponent ngOnChanges called:', changes);
    if (changes['isOpen'] && this.isOpen) {
      console.log('📊 Modal is opening, refreshing data...');
      console.log('📱 Platform:', Capacitor.getPlatform());
      
      // For iOS, use force refresh to ensure data loads properly
      if (Capacitor.getPlatform() === 'ios') {
        console.log('📱 iOS detected, using force refresh...');
        this.wordUsageService.forceRefreshData().then(() => {
          this.currentStats = this.wordUsageService.getUsageStats();
          this.currentSettings = this.wordUsageService.getQuotaSettings();
          console.log('📊 iOS Modal data refreshed:', {
            stats: this.currentStats,
            settings: this.currentSettings,
          });
        });
      } else {
        // Standard refresh for other platforms
        this.wordUsageService.refreshData();
        this.currentStats = this.wordUsageService.getUsageStats();
        this.currentSettings = this.wordUsageService.getQuotaSettings();
        console.log('📊 Modal data refreshed:', {
          stats: this.currentStats,
          settings: this.currentSettings,
        });
      }
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
  }

  onClose(): void {
    this.editingSettings = false;
    this.close.emit();
  }

  startEditingSettings(): void {
    this.editingSettings = true;
    if (this.currentSettings) {
      this.editForm = { ...this.currentSettings };
    }
  }

  saveSettings(): void {
    if (this.currentSettings) {
      const updatedSettings: QuotaSettings = {
        ...this.editForm,
      };

      this.wordUsageService.updateQuotaSettings(updatedSettings).subscribe({
        next: (data) => {
          console.log('✅ Quota settings updated successfully:', data);
          this.editingSettings = false;
        },
        error: (error) => {
          console.error('❌ Error updating quota settings:', error);
          alert('Failed to update quota settings. Please try again.');
        },
      });
    }
  }

  cancelEditingSettings(): void {
    this.editingSettings = false;
    if (this.currentSettings) {
      this.editForm = { ...this.currentSettings };
    }
  }

  resetUsage(): void {
    if (
      confirm(
        'Are you sure you want to reset your usage statistics? This action cannot be undone.'
      )
    ) {
      this.wordUsageService.resetUsageStats().subscribe({
        next: (stats) => {
          console.log('✅ Usage statistics reset successfully:', stats);
          alert('Usage statistics have been reset successfully.');
        },
        error: (error) => {
          console.error('❌ Error resetting usage statistics:', error);
          alert('Failed to reset usage statistics. Please try again.');
        },
      });
    }
  }

  getUsageDisplay() {
    if (!this.currentStats) return null;

    // Return cached value if available and view hasn't changed
    if (
      this._cachedUsageDisplay &&
      this._cachedUsageDisplay.showRemainingView === this.showRemainingView
    ) {
      return this._cachedUsageDisplay;
    }

    // Calculate and cache the result
    this._cachedUsageDisplay = {
      ...this.wordUsageService.formatUsage(
        this.currentStats,
        this.showRemainingView,
        this.wordsUsedAtLastPurchase
      ),
      showRemainingView: this.showRemainingView, // Store the view state with cache
    };

    return this._cachedUsageDisplay;
  }

  getProgressBarColor(): string {
    if (!this.currentStats || !this.currentSettings) return 'bg-gray-300';

    if (this.currentStats.isOverLimit) return 'bg-red-500';

    let wordPercentage = 0;

    if (this.showRemainingView) {
      // Remaining view: show countdown from 0 (remaining words from total limit)
      const wordsRemaining = Math.max(
        0,
        this.currentSettings.wordLimit - this.currentStats.wordsUsed
      );
      wordPercentage =
        this.currentSettings.wordLimit > 0
          ? (wordsRemaining / this.currentSettings.wordLimit) * 100
          : 0;
    } else {
      // Total view: calculate based on total usage vs total limit
      wordPercentage =
        this.currentSettings.wordLimit > 0
          ? (this.currentStats.wordsUsed / this.currentSettings.wordLimit) * 100
          : 0;
    }

    if (wordPercentage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  }

  getProgressBarWidth(): number {
    if (!this.currentStats || !this.currentSettings) return 0;

    if (this.showRemainingView) {
      // Remaining view: show countdown from 0 (remaining words from total limit)
      const wordsRemaining = Math.max(
        0,
        this.currentSettings.wordLimit - this.currentStats.wordsUsed
      );
      const percentage =
        this.currentSettings.wordLimit > 0
          ? (wordsRemaining / this.currentSettings.wordLimit) * 100
          : 0;
      return Math.min(percentage, 100);
    } else {
      // Total view: calculate based on total usage vs total limit
      const wordPercentage =
        this.currentSettings.wordLimit > 0
          ? (this.currentStats.wordsUsed / this.currentSettings.wordLimit) * 100
          : 0;
      return Math.min(wordPercentage, 100);
    }
  }

  setToggleView(showRemaining: boolean): void {
    // Only update if the value is actually changing
    if (this.showRemainingView === showRemaining) {
      return;
    }

    this.showRemainingView = showRemaining;

    // Clear the cached display to force recalculation
    this._cachedUsageDisplay = null;

    console.log(
      '🔄 Toggle view changed to:',
      this.showRemainingView ? 'Remaining' : 'Total'
    );
    console.log('📊 Current stats:', this.currentStats);
    console.log(
      '📊 Current wordsUsedAtLastPurchase:',
      this.wordsUsedAtLastPurchase
    );

    // Save the toggle preference to the database
    this.wordUsageService
      .updateTogglePreference(this.showRemainingView)
      .subscribe({
        next: (response) => {
          console.log('✅ Toggle preference saved:', this.showRemainingView);
          console.log('📊 Response data:', response.data);

          // Update local values from response
          if (response.data) {
            this.wordsUsedAtLastPurchase =
              response.data.wordsUsedAtLastPurchase || 0;
            console.log(
              '📊 Updated wordsUsedAtLastPurchase:',
              this.wordsUsedAtLastPurchase
            );
          }

          // Force refresh of the display
          if (this.currentStats) {
            console.log('🔄 Refreshing display with new values');
            // Trigger change detection by updating the stats reference
            this.currentStats = { ...this.currentStats };
          }
        },
        error: (error) => {
          console.error('❌ Error saving toggle preference:', error);
        },
      });
  }
}
